import { useCallback, useRef, type ReactNode } from 'react';
import { Download } from 'lucide-react';
import { QRCodeCanvas } from 'qrcode.react';
import type { BackofficeLinkQrCodeSettings } from '../types';

type LinkQrCodePanelProps = {
  slug: string;
  shortUrl: string;
  qrCode: BackofficeLinkQrCodeSettings;
  headerActions?: ReactNode;
  className?: string;
  showDownloadButton?: boolean;
  detailsContent?: ReactNode;
};

const QR_LOGO_SRC = '/nava-icon-square.svg';
const QR_LOGO_WIDTH = 36;
const QR_LOGO_HEIGHT = 36;

export function LinkQrCodePanel({
  slug,
  shortUrl,
  qrCode,
  headerActions,
  className,
  showDownloadButton = true,
  detailsContent,
}: LinkQrCodePanelProps) {
  const qrRef = useRef<HTMLDivElement>(null);

  const handleDownload = useCallback(() => {
    const canvas = qrRef.current?.querySelector('canvas');
    if (!canvas) return;

    const exportCanvas = document.createElement('canvas');
    const padding = 40;
    const size = canvas.width + padding * 2;
    exportCanvas.width = size;
    exportCanvas.height = size;

    const context = exportCanvas.getContext('2d');
    if (!context) return;

    context.fillStyle = '#ffffff';
    context.fillRect(0, 0, size, size);
    context.drawImage(canvas, padding, padding);

    const link = document.createElement('a');
    link.download = `link-qr-${slug}.png`;
    link.href = exportCanvas.toDataURL('image/png');
    link.click();
  }, [slug]);

  return (
    <section
      className={['rounded-xl border border-neutral-200 bg-neutral-50 p-4', className]
        .filter(Boolean)
        .join(' ')}
    >
      <div className="flex flex-col gap-4 md:flex-row md:items-center">
        <div
          ref={qrRef}
          className="flex w-fit shrink-0 items-center justify-center rounded-xl bg-white p-3 shadow-sm"
        >
          <QRCodeCanvas
            value={shortUrl}
            size={180}
            level="H"
            includeMargin={false}
            fgColor={qrCode.foregroundColor}
            {...(qrCode.showLogo
              ? {
                  imageSettings: {
                    src: QR_LOGO_SRC,
                    height: QR_LOGO_HEIGHT,
                    width: QR_LOGO_WIDTH,
                    excavate: true,
                    crossOrigin: 'anonymous',
                  },
                }
              : {})}
          />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-start justify-between gap-3">
            <div>
              <h3 className="font-inter-tight text-lg font-semibold text-neutral-900">QR code</h3>
              <p className="mt-1 text-small text-neutral-600">
                Scan or share this code to open the short link directly.
              </p>
            </div>
            {headerActions ? <div className="shrink-0">{headerActions}</div> : null}
          </div>
          {detailsContent ? (
            <div className="mt-3">{detailsContent}</div>
          ) : (
            <div className="mt-3 flex flex-wrap items-center gap-2 text-small text-neutral-600">
              <span className="inline-flex rounded-full bg-white px-2.5 py-1 ring-1 ring-neutral-200">
                Color: {qrCode.foregroundColor}
              </span>
              <span className="inline-flex rounded-full bg-white px-2.5 py-1 ring-1 ring-neutral-200">
                Logo: {qrCode.showLogo ? 'Shown' : 'Hidden'}
              </span>
            </div>
          )}
          {showDownloadButton ? (
            <button
              type="button"
              onClick={handleDownload}
              className="focus-ring mt-4 inline-flex h-10 items-center gap-1.5 whitespace-nowrap rounded-lg border border-neutral-200 bg-white px-4 text-small font-medium text-neutral-700 hover:bg-backoffice-50"
            >
              <Download className="h-4 w-4" aria-hidden="true" />
              Download QR code
            </button>
          ) : null}
        </div>
      </div>
    </section>
  );
}
